/**
 * @classdesc Classe com regras de negócio para tratamento de impedimentos.
 * Fornece métodos para verificar presença de magistrados na composição da sessão e
 * para filtrar impedimentos conforme sua situação (dentro/fora da composição).
 * @readonly
 */
export default class ImpedimentoBusiness {
    /**
     * Verifica se uma sigla de magistrado está presente na composição da sessão.
     *
     * @private
     * @param {Array} composicao - Lista de objetos da composição (cada objeto contém magistrado).
     * @param {string} sigla - Sigla do magistrado a ser verificada.
     * @returns {boolean} - Verdadeiro se a sigla for encontrada (como titular ou substituto).
     */
    static #contemSiglaNaComposicao(composicao, sigla) {
        if (!composicao || composicao.length === 0) {
            return false;
        }

        return composicao.some(
            (item) => item.magistrado?.sigla === sigla || item.magistrado?.siglaSubstituto === sigla
        );
    }

    /**
     * Verifica se a sigla do magistrado está presente na composição.
     *
     * @private
     * @param {Array} composicao - Lista de objetos da composição.
     * @param {string} sigla - Sigla do magistrado.
     * @returns {boolean}
     */
    static #isSiglaNaComposicao(composicao, sigla) {
        return this.#contemSiglaNaComposicao(composicao, sigla);
    }

    /**
     * Verifica se a sigla do magistrado NÃO está presente na composição.
     *
     * @private
     * @param {Array} composicao - Lista de objetos da composição.
     * @param {string} sigla - Sigla do magistrado.
     * @returns {boolean}
     */
    static #isSiglaForaComposicao(composicao, sigla) {
        return !this.#contemSiglaNaComposicao(composicao, sigla);
    }

    /**
     * Retorna os impedimentos cujo magistrado pertence à composição da sessão.
     *
     * @param {Array} composicao - Lista da composição.
     * @param {Array} impedimentos - Lista de impedimentos (cada um com magistrado.sigla).
     * @returns {Array} - Impedimentos cujo magistrado está na composição.
     */
    static getImpedimentosNaComposicao(composicao, impedimentos) {
        if (!impedimentos || impedimentos.length === 0) {
            return [];
        }

        return impedimentos.filter(
            (impedimento) =>
                impedimento.magistrado?.sigla && this.#isSiglaNaComposicao(composicao, impedimento.magistrado.sigla)
        );
    }

    /**
     * Retorna os impedimentos cujo magistrado NÃO pertence à composição da sessão.
     *
     * @param {Array} composicao - Lista da composição.
     * @param {Array} impedimentos - Lista de impedimentos.
     * @returns {Array} - Impedimentos cujo magistrado está fora da composição.
     */
    static getImpedimentosForaComposicao(composicao, impedimentos) {
        if (!impedimentos || impedimentos.length === 0) {
            return [];
        }

        return impedimentos.filter(
            (impedimento) =>
                impedimento.magistrado?.sigla && this.#isSiglaForaComposicao(composicao, impedimento.magistrado.sigla)
        );
    }

    /**
     * Filtra os impedimentos declarados (situacaoImpedimento = 'IMPEDIMENTO_DECLARADO') que estão na composição.
     *
     * @param {Array} composicao - Lista da composição.
     * @param {Array} impedimentos - Lista de impedimentos.
     * @returns {Array}
     */
    static getImpedimentosDeclaradosNaComposicao(composicao, impedimentos) {
        if (!impedimentos || impedimentos.length === 0) {
            return [];
        }

        return this.getImpedimentosNaComposicao(composicao, impedimentos).filter(
            (imp) => imp.situacaoImpedimento === 'IMPEDIMENTO_DECLARADO'
        );
    }

    /**
     * Filtra os impedimentos declarados (situacaoImpedimento = 'IMPEDIMENTO_DECLARADO') que estão fora da composição.
     *
     * @param {Array} composicao - Lista da composição.
     * @param {Array} impedimentos - Lista de impedimentos.
     * @returns {Array}
     */
    static getImpedimentosDeclaradosForaComposicao(composicao, impedimentos) {
        if (!impedimentos || impedimentos.length === 0) {
            return [];
        }

        return this.getImpedimentosForaComposicao(composicao, impedimentos).filter(
            (imp) => imp.situacaoImpedimento === 'IMPEDIMENTO_DECLARADO'
        );
    }

    /**
     * Filtra os impedimentos possíveis (situacaoImpedimento = 'IMPEDIMENTO_POSSIVEL') que estão na composição.
     *
     * @param {Array} composicao - Lista da composição.
     * @param {Array} impedimentos - Lista de impedimentos.
     * @returns {Array}
     */
    static getImpedimentosPossiveisNaComposicao(composicao, impedimentos) {
        if (!impedimentos || impedimentos.length === 0) {
            return [];
        }

        return this.getImpedimentosNaComposicao(composicao, impedimentos).filter(
            (imp) => imp.situacaoImpedimento === 'IMPEDIMENTO_POSSIVEL'
        );
    }

    /**
     * Filtra os impedimentos possíveis (situacaoImpedimento = 'IMPEDIMENTO_POSSIVEL') que estão fora da composição.
     *
     * @param {Array} composicao - Lista da composição.
     * @param {Array} impedimentos - Lista de impedimentos.
     * @returns {Array}
     */
    static getImpedimentosPossiveisForaComposicao(composicao, impedimentos) {
        if (!impedimentos || impedimentos.length === 0) {
            return [];
        }

        return this.getImpedimentosForaComposicao(composicao, impedimentos).filter(
            (imp) => imp.situacaoImpedimento === 'IMPEDIMENTO_POSSIVEL'
        );
    }

    /**
     * Verifica se um magistrado está declarado como impedido E pertence à composição.
     *
     * @param {Array} composicao - Lista da composição.
     * @param {Array} impedimentos - Lista de impedimentos.
     * @param {string} siglaMagistrado - Sigla do magistrado a verificar.
     * @returns {boolean} - Verdadeiro se o magistrado estiver na composição e houver impedimento declarado.
     */
    static isImpedidoNaComposicao(composicao, impedimentos, siglaMagistrado) {
        if (!impedimentos || impedimentos.length === 0) {
            return false;
        }

        return this.getImpedimentosDeclaradosNaComposicao(composicao, impedimentos).some(
            (imp) => imp.magistrado?.sigla === siglaMagistrado
        );
    }

    /**
     * Verifica se um magistrado está possivelmente impedido E pertence à composição.
     *
     * @param {Array} composicao - Lista da composição.
     * @param {Array} impedimentos - Lista de impedimentos.
     * @param {string} siglaMagistrado - Sigla do magistrado a verificar.
     * @returns {boolean} - Verdadeiro se o magistrado estiver na composição e houver impedimento possível.
     */
    static isPossivelmenteImpedidoNaComposicao(composicao, impedimentos, siglaMagistrado) {
        if (!impedimentos || impedimentos.length === 0) {
            return false;
        }

        return this.getImpedimentosPossiveisNaComposicao(composicao, impedimentos).some(
            (imp) => imp.magistrado?.sigla === siglaMagistrado
        );
    }
}
